import { type AnyFieldApi, createForm } from "@tanstack/solid-form";
import axios from "axios";
import {
  BiRegularDownload,
  BiRegularFile,
  BiRegularUpload,
  BiRegularX,
} from "solid-icons/bi";
import { FiAlertCircle, FiCheckCircle } from "solid-icons/fi";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  type ParentComponent,
  Show,
} from "solid-js";
import { toast } from "solid-sonner";
import z from "zod";
import { useI18n } from "@/api/i18n";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  UPLOAD_RULES,
  type UploadRule,
} from "@/domains/documents/constants/upload";
import { useAssetDownload } from "@/domains/documents/hooks/use-asset-download";
import {
  formatUploadedFileSize,
  getAcceptAttribute,
  getAllowedTypesLabel,
  getMaxSizeMb,
  partitionUploadFiles,
  type RejectedUploadFile,
  validateUploadFile,
} from "@/domains/documents/utils/file-validation";
import {
  getUploadApiError,
  mapUploadErrorMessage,
} from "@/domains/documents/utils/upload-error";
import { useUploadUserResume } from "@/domains/freelance/profile/api";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import {
  formatAssetDate,
  formatAssetFileSize,
  truncateFilename,
} from "@/libs/assets";
import { tryCatch } from "@/libs/try-catch";
import type { Asset } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";

type FileInputProps = {
  onFileChange: (event: Event) => void;
  fieldApi: AnyFieldApi;
  currentResume?: Asset | null;
  onRemoveFile?: () => void;
  /** Drives client pre-validation and derives the `accept` attribute + formats hint (T4). */
  uploadRules: UploadRule;
};

type MultiFileInputProps = {
  onFileChange: (event: Event) => void;
  fieldApi: AnyFieldApi;
  onRemoveFile?: (index: number) => void;
  maxFiles?: number;
  /** Drives per-file client pre-validation and derives the `accept` attribute + formats hint (T4). */
  uploadRules: UploadRule;
};

export const MultiFileInput: Component<MultiFileInputProps> = (props) => {
  const { t } = useI18n();

  let fileInputRef: HTMLInputElement | undefined;
  const inputId = props.fieldApi.name ?? "multi-file-upload";

  const files = createMemo(() => {
    const value = props.fieldApi.state.value;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  });

  // Files rejected by client pre-validation in the last selection (T4).
  const [rejected, setRejected] = createSignal<RejectedUploadFile[]>([]);
  const [justAddedCount, setJustAddedCount] = createSignal(0);

  const acceptValue = () => getAcceptAttribute(props.uploadRules);

  const formatsLabel = () => getAllowedTypesLabel(props.uploadRules);

  const describeRejection = (item: RejectedUploadFile): string => {
    const name = item.file.name;
    switch (item.reason) {
      case "size":
        return t("common.upload_errors.rejected_reason_size", {
          name,
          size: formatUploadedFileSize(item.file),
          max: getMaxSizeMb(props.uploadRules),
        });
      case "type":
        return t("common.upload_errors.rejected_reason_type", {
          name,
          types: formatsLabel(),
        });
      default:
        return t("common.upload_errors.rejected_reason_empty", { name });
    }
  };

  const handleFilesSelection = (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const currentFiles = files();
    const maxFiles = props.maxFiles ?? Number.POSITIVE_INFINITY;

    // Validate each file client-side. This runs for both the picker and the drop path
    // (both funnel through here — R4). Valid files enter the field up to maxFiles;
    // rejected files feed the inline alert (AC 3). The alert resets on the next
    // selection because setRejected always reflects the latest partition.
    const partition = partitionUploadFiles(fileArray, props.uploadRules);
    setRejected(partition.rejected);

    const combinedFiles = [...currentFiles, ...partition.accepted].slice(
      0,
      maxFiles,
    );
    setJustAddedCount(combinedFiles.length - currentFiles.length);

    props.fieldApi.handleChange?.(combinedFiles);
    props.onFileChange({
      target: { files: newFiles },
    } as unknown as Event);
  };

  const {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFilesDrop: handleFilesSelection,
  });

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const selectedFiles = target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFilesSelection(selectedFiles);
    }
    if (fileInputRef) {
      fileInputRef.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const currentFiles = files();
    const updatedFiles = currentFiles.filter((_, i) => i !== index);
    props.fieldApi.handleChange?.(
      updatedFiles.length > 0 ? updatedFiles : undefined,
    );
    props.onRemoveFile?.(index);
    if (fileInputRef) {
      fileInputRef.value = "";
    }
  };

  const formattedFileSize = (file: File) => {
    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB >= 0.1) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInKB = file.size / 1024;
    return `${sizeInKB.toFixed(1)} KB`;
  };

  const maxFiles = props.maxFiles ?? Number.POSITIVE_INFINITY;
  const canAddMore = files().length < maxFiles;

  return (
    <div class="mt-2 space-y-4 rounded-md border border-gray-200 p-2">
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        name={props.fieldApi.name}
        accept={acceptValue()}
        multiple
        class="sr-only"
        onChange={handleInputChange}
        disabled={!canAddMore}
      />

      <Show when={canAddMore}>
        <button
          type="button"
          class={
            "relative flex min-h-[180px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 hover:border-primary/60 hover:bg-primary/5"
          }
          classList={{
            "border-primary bg-primary/5": isDragging(),
            "border-gray-300 bg-gray-50": !isDragging(),
          }}
          onClick={() => fileInputRef?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <BiRegularUpload
            class="mx-auto h-12 w-12 text-gray-400 transition-colors"
            classList={{ "text-primary": isDragging() }}
          />
          <span
            class="mt-3 text-sm font-medium text-gray-900 transition-colors"
            classList={{ "text-primary": isDragging() }}
          >
            {t("common.resume_upload.select_or_drag")}
          </span>
          <Show
            when={files().length > 0}
            fallback={
              <span class="mt-1 text-xs text-gray-500">
                {t("common.resume_upload.no_file_selected")}
              </span>
            }
          >
            <span class="mt-1 text-xs text-gray-500">
              {files().length} file{files().length !== 1 ? "s" : ""} selected
              {props.maxFiles &&
                props.maxFiles !== Number.POSITIVE_INFINITY &&
                ` (max ${props.maxFiles})`}
            </span>
          </Show>
        </button>
      </Show>

      <Show when={files().length > 0}>
        <div class="space-y-2">
          <For each={files()}>
            {(file, index) => (
              <div class="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
                <div class="flex min-w-0 items-center gap-3">
                  <span class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <BiRegularFile size={18} />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-medium text-gray-900">
                      {file.name}
                    </p>
                    <p class="text-xs text-gray-500">
                      {formattedFileSize(file)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => handleRemoveFile(index())}
                  aria-label={
                    t("common.resume_upload.remove_current") as string
                  }
                >
                  <BiRegularX size={16} />
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={rejected().length > 0}>
        <Alert variant="destructive">
          <FiAlertCircle size={16} />
          <AlertTitle>
            {t("common.upload_errors.rejected_summary", {
              count: rejected().length,
            })}
          </AlertTitle>
          <AlertDescription>
            <ul class="mt-1 list-inside list-disc space-y-0.5">
              <For each={rejected()}>
                {(item) => <li>{describeRejection(item)}</li>}
              </For>
            </ul>
            <Show when={justAddedCount() > 0}>
              <div class="mt-1 text-destructive/80">
                {t("common.upload_errors.accepted_summary", {
                  count: justAddedCount(),
                })}
              </div>
            </Show>
          </AlertDescription>
        </Alert>
      </Show>

      <div class="overflow-hidden rounded-lg">
        <div class="flex flex-wrap gap-4 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <div class="flex items-center gap-3">
            <span class="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground">
              <FiCheckCircle size={14} />
            </span>
            <span class="text-xs font-medium text-gray-700">
              {t("common.file_upload.custom_allowed_file_types", {
                types: formatsLabel(),
              })}
            </span>
          </div>

          <div class="flex items-center gap-3">
            <span class="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground">
              <FiCheckCircle size={14} />
            </span>
            <span class="text-xs font-medium text-gray-700">
              {t("common.resume_upload.allowed_file_size", {
                size: getMaxSizeMb(props.uploadRules),
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FileInput: Component<FileInputProps> = (props) => {
  const { t } = useI18n();
  const { download, isPending } = useAssetDownload();

  const resumeMeta = (asset: Asset) => {
    const parts: string[] = [];
    if (asset.size_bytes != null)
      parts.push(formatAssetFileSize(asset.size_bytes));
    parts.push(formatAssetDate(asset.created_at));
    return parts.join(" • ");
  };

  let fileInputRef: HTMLInputElement | undefined;
  const inputId = props.fieldApi.name ?? "resume-upload";

  // A persisted/serialized field value can rehydrate as a non-File object
  // (a File can't survive JSON serialization), which would otherwise render a
  // broken "NaN KB" entry. Only treat real File instances as a selected file.
  const selectedFile = createMemo(() =>
    props.fieldApi.state.value instanceof File
      ? (props.fieldApi.state.value as File)
      : null,
  );

  // File rejected by client pre-validation in the last selection (T4).
  const [singleRejected, setSingleRejected] =
    createSignal<RejectedUploadFile | null>(null);

  const acceptValue = () => getAcceptAttribute(props.uploadRules);

  const formatsLabel = () => getAllowedTypesLabel(props.uploadRules);

  const handleFileSelection = (file: File) => {
    // Validate client-side; covers both the picker (routed here from handleInputChange)
    // and the drop path (R4). An invalid file never enters the field, and the alert
    // clears on the next valid selection (AC 4).
    const result = validateUploadFile(file, props.uploadRules);
    if (!result.ok) {
      setSingleRejected({ file, reason: result.reason });
      return;
    }
    setSingleRejected(null);
    props.fieldApi.handleChange?.(file);
    props.onFileChange({
      target: { files: [file] },
    } as unknown as Event);
  };

  const {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFileDrop: handleFileSelection,
  });

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      handleFileSelection(file);
    } else {
      props.fieldApi.handleChange?.(undefined);
      props.onFileChange(event);
    }
    // Reset so re-selecting the same file (e.g. after a rejection) fires onChange again.
    if (fileInputRef) {
      fileInputRef.value = "";
    }
  };

  const handleRemoveFile = () => {
    props.fieldApi.handleChange?.(undefined);
    props.onRemoveFile?.();
    if (fileInputRef) {
      fileInputRef.value = "";
    }
  };

  const formattedFileSize = () => {
    const value = selectedFile();
    if (!value) return "";
    const sizeInMB = value.size / (1024 * 1024);
    if (sizeInMB >= 0.1) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInKB = value.size / 1024;
    return `${sizeInKB.toFixed(1)} KB`;
  };

  const singleRejectionTitle = (item: RejectedUploadFile): string => {
    switch (item.reason) {
      case "size":
        return t("common.upload_errors.title_file_too_large");
      case "empty":
        return t("common.upload_errors.title_empty_file");
      default:
        return t("common.upload_errors.title_invalid_type");
    }
  };

  const singleRejectionDescription = (item: RejectedUploadFile): string => {
    switch (item.reason) {
      case "size":
        return t("common.upload_errors.client_too_large", {
          name: item.file.name,
          size: formatUploadedFileSize(item.file),
          max: getMaxSizeMb(props.uploadRules),
        });
      case "empty":
        return t("common.upload_errors.client_empty", { name: item.file.name });
      default:
        return t("common.upload_errors.client_invalid_type", {
          name: item.file.name,
          types: formatsLabel(),
        });
    }
  };

  return (
    <div class="mt-2 space-y-4 rounded-md border border-gray-200 p-2">
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        name={props.fieldApi.name}
        accept={acceptValue()}
        class="sr-only"
        onChange={handleInputChange}
      />

      <button
        type="button"
        class={
          "relative flex min-h-[180px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 hover:border-primary/60 hover:bg-primary/5"
        }
        classList={{
          "border-primary bg-primary/5": isDragging(),
          "border-gray-300 bg-gray-50": !isDragging(),
        }}
        onClick={() => fileInputRef?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <BiRegularUpload
          class="mx-auto h-12 w-12 text-gray-400 transition-colors"
          classList={{ "text-primary": isDragging() }}
        />
        <span
          class="mt-3 text-sm font-medium text-gray-900 transition-colors"
          classList={{ "text-primary": isDragging() }}
        >
          {t("common.resume_upload.select_or_drag")}
        </span>
        <Show
          when={selectedFile()}
          fallback={
            <span class="mt-1 text-xs text-gray-500">
              {t("common.resume_upload.no_file_selected")}
            </span>
          }
        >
          {(file) => (
            <span class="mt-1 block truncate text-xs text-gray-500">
              {file().name}
            </span>
          )}
        </Show>
      </button>

      <Show when={selectedFile()}>
        {(file) => (
          <div class="mt-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
            <div class="flex min-w-0 items-center gap-3">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <BiRegularFile size={18} />
              </span>
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-gray-900">
                  {file().name}
                </p>
                <p class="text-xs text-gray-500">{formattedFileSize()}</p>
              </div>
            </div>
            <button
              type="button"
              class="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
              onClick={handleRemoveFile}
              aria-label={t("common.resume_upload.remove_current") as string}
            >
              <BiRegularX size={16} />
            </button>
          </div>
        )}
      </Show>

      <Show when={singleRejected()}>
        {(rejection) => (
          <Alert variant="destructive">
            <FiAlertCircle size={16} />
            <AlertTitle>{singleRejectionTitle(rejection())}</AlertTitle>
            <AlertDescription>
              {singleRejectionDescription(rejection())}
            </AlertDescription>
          </Alert>
        )}
      </Show>

      <div class="overflow-hidden rounded-lg">
        <div class="flex flex-wrap gap-4 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <div class="flex items-center gap-3">
            <span class="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground">
              <FiCheckCircle size={14} />
            </span>
            <span class="text-xs font-medium text-gray-700">
              {t("common.file_upload.custom_allowed_file_types", {
                types: formatsLabel(),
              })}
            </span>
          </div>

          <div class="flex items-center gap-3">
            <span class="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground">
              <FiCheckCircle size={14} />
            </span>
            <span class="text-xs font-medium text-gray-700">
              {t("common.resume_upload.allowed_file_size", {
                size: getMaxSizeMb(props.uploadRules),
              })}
            </span>
          </div>
        </div>

        <Show when={props.currentResume}>
          {(asset) => (
            <div class="border-t border-gray-200 px-4 py-2 mt-2">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {truncateFilename(asset().filename)}
                  </p>
                  <p class="text-xs text-gray-500">{resumeMeta(asset())}</p>
                </div>
                <Button
                  onClick={() => download(asset().id)}
                  disabled={isPending(asset().id)}
                  aria-busy={isPending(asset().id)}
                  variant="outline"
                  size="sm"
                  class="gap-2"
                >
                  <BiRegularDownload size={16} />
                  {t("common.file_upload.view_current")}
                </Button>
              </div>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

const resumeModalSchema = z.object({
  resume: z.instanceof(File),
});

type TranslateFn = ReturnType<typeof useI18n>["t"];

export type InlineUploadError = { title: string; description: string };

const uploadServerTitle = (t: TranslateFn, code: string): string => {
  switch (code) {
    case "LIMIT_FILE_SIZE":
      return t("common.upload_errors.title_file_too_large");
    case "INVALID_FILE_TYPE":
      return t("common.upload_errors.title_invalid_type");
    default:
      return t("common.upload_errors.title_generic");
  }
};

/**
 * Resolve the inline error to show next to the resume file input, or `null` to keep a
 * generic toast (AC 10). Recognized upload errors (413/415/400 `LIMIT_*`) are mapped to
 * a code-keyed message (T2, AC 7/9); a legacy `400` without an upload code keeps the
 * historical "invalid resume" inline message. Anything else → `null`.
 */
export const getResumeUploadInlineError = (
  error: unknown,
  t: TranslateFn,
  fallbackInvalidMessage: string,
): InlineUploadError | null => {
  const uploadError = getUploadApiError(error);
  if (uploadError) {
    return {
      title: uploadServerTitle(t, uploadError.code),
      description: mapUploadErrorMessage(uploadError.code, t, {
        allowedTypesLabel: getAllowedTypesLabel(UPLOAD_RULES.resume),
      }),
    };
  }
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    return {
      title: t("common.upload_errors.title_invalid_type"),
      description: fallbackInvalidMessage,
    };
  }
  return null;
};

export const UploadResumeModal: ParentComponent<{
  currentResume?: Asset | null;
  onUpload?: (resume?: Asset | null) => void;
}> = (props) => {
  const [open, setOpen] = createSignal(false);
  const [uploadError, setUploadError] = createSignal<InlineUploadError | null>(
    null,
  );
  const uploadResume = useUploadUserResume();
  const { t } = useI18n();
  const form = createForm(() => ({
    defaultValues: {
      resume: undefined as File | undefined,
    },
    schema: resumeModalSchema,
    onSubmit: async ({ value }) => {
      setUploadError(null);
      toast.loading(t("common.resume_upload.loading"), {
        id: "resume-upload-modal",
      });
      const validatedData = resumeModalSchema.safeParse(value);
      if (!validatedData.success) {
        toast.error(t("common.error"), {
          id: "resume-upload-modal",
        });
        return;
      }
      const [response, error] = await tryCatch(
        uploadResume.mutateAsync({ resume: validatedData.data.resume }),
      );
      if (error) {
        const inlineError = getResumeUploadInlineError(
          error,
          t,
          t("common.file_upload.invalid_resume"),
        );
        if (inlineError) {
          // Recognized upload error (mapped by code) or legacy invalid file: dismiss the
          // loading toast, show the message inline, and drop the selected file so the
          // user can reselect immediately (AC 4).
          toast.dismiss("resume-upload-modal");
          setUploadError(inlineError);
          form.setFieldValue("resume", undefined);
          return;
        }
        toast.error(t("common.error"), {
          id: "resume-upload-modal",
        });
        return;
      }
      toast.success(t("common.file_upload.success"), {
        id: "resume-upload-modal",
      });
      props.onUpload?.(response.asset ?? undefined);
      form.reset();
      setOpen(false);
    },
  }));

  const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      setUploadError(null);
      form.setFieldValue("resume", file);
    }
  };

  const handleRemoveFile = () => {
    form.setFieldValue("resume", undefined);
  };

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger>{props.children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("common.file_upload.upload")}</DialogTitle>
        </DialogHeader>

        <form.Field
          name="resume"
          validators={{
            onChange: resumeModalSchema.shape.resume,
          }}
        >
          {(field) => (
            <FileInput
              currentResume={props.currentResume}
              fieldApi={field()}
              onFileChange={handleFileChange}
              onRemoveFile={handleRemoveFile}
              uploadRules={UPLOAD_RULES.resume}
            />
          )}
        </form.Field>

        <Show when={uploadError()}>
          {(error) => (
            <Alert variant="destructive">
              <FiAlertCircle size={16} />
              <AlertTitle>{error().title}</AlertTitle>
              <AlertDescription>{error().description}</AlertDescription>
            </Alert>
          )}
        </Show>

        <DialogFooter>
          <Button variant="neutralOutline" onClick={() => form.reset()}>
            {t("common.cancel")}
          </Button>
          <form.Subscribe
            selector={(state) => ({
              // An invalid file (too large / wrong type) is rejected client-side and never
              // enters the field, so gating on "a real File is selected" blocks the upload
              // button for both the empty and the rejected cases.
              hasValidResume: state.values.resume instanceof File,
              isSubmitting: state.isSubmitting,
            })}
          >
            {(state) => (
              <Button
                variant="neutral"
                onClick={() => form.handleSubmit()}
                disabled={!state().hasValidResume || state().isSubmitting}
              >
                {state().isSubmitting
                  ? t("common.loading")
                  : t("common.file_upload.upload")}
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
